import { IAccountSummaryChart, IReportConfiguration, isOtherSliceId } from '@opengov/reporting-types';
import { Draft, castDraft } from 'immer';

type AccountSummaryDraft = NonNullable<Draft<IReportConfiguration>['accountSummary']>;

/**
 * Every `seriesColors` key is `${groupId}` or `${groupId}.${...}`, where the group is one of the
 * report's row groups or column groups — a whole column group for a calculated column, a column
 * value, or a row-group value.
 *
 * A key that the currently rendered chart does not address is **not** stale. Chart type and axis
 * source together decide whether rows or columns supply the series, so the same report legitimately
 * accumulates keys of both shapes; switching away from a configuration and back, or opening a saved
 * view configured differently, must find its colors intact. A key only becomes unreachable when the
 * group naming it is deleted, because from then on nothing the report can render will produce that
 * prefix again.
 */
export function isSeriesColorKeyReachable(categoryId: string, groupIds: ReadonlySet<string>): boolean {
  // The synthesized pie/donut "Other" slice aggregates categories rather than belonging to a group,
  // so no group deletion can orphan it. Clicking it raises a toast instead of saving a color, but
  // guard it anyway so a key that reached the config some other way is never mistaken for garbage.
  if (isOtherSliceId(categoryId)) {
    return true;
  }
  return groupIds.has(categoryId.split('.')[0]);
}

export function getReachableSeriesColors(
  seriesColors: IAccountSummaryChart['seriesColors'],
  groupIds: ReadonlySet<string>
): NonNullable<IAccountSummaryChart['seriesColors']> {
  return Object.fromEntries(
    Object.entries(seriesColors ?? {}).filter(([categoryId]) => isSeriesColorKeyReachable(categoryId, groupIds))
  );
}

export function getReportGroupIds(accountSummary: AccountSummaryDraft): Set<string> {
  const groups: { id?: string }[] = [...accountSummary.rowGroups, ...accountSummary.columnGroups];
  return new Set(groups.map((group) => group.id).filter((id): id is string => Boolean(id)));
}

function hasAnySeriesColors(charts: AccountSummaryDraft['charts']): boolean {
  return (charts ?? []).some((chart) => Object.keys(chart.seriesColors ?? {}).length > 0);
}

/**
 * Drops the saved colors of series whose owning row or column group no longer exists on the report.
 * Call this after a group is removed; it is a no-op for every key whose group survived.
 */
export function pruneSeriesColorsForDeletedGroups(accountSummary?: AccountSummaryDraft): void {
  if (!accountSummary || !hasAnySeriesColors(accountSummary.charts)) {
    return;
  }

  const groupIds = getReportGroupIds(accountSummary);
  (accountSummary.charts ?? []).forEach((chart) => {
    if (!chart.seriesColors) {
      return;
    }
    chart.seriesColors = castDraft(getReachableSeriesColors(chart.seriesColors, groupIds));
  });
}
